import React, { useEffect, useRef, useState } from 'react';
import './style.scss';
import useWs from '../../../../../contexts/websockets';
import Button from '../../../../controls/button';
import { useWsToHttp } from '../../../../../hooks/wsToHttp/useWsToHttp';
import { getRandomElement, wsActionTypes } from 'shared';
import { IFightNft } from '../../../../../interfaces/FightNft';
import clsx from 'clsx';

interface Props {
  bet: number;
  nft: IFightNft;
  next: () => void;
}

interface Error {
  status: boolean;
  activeFight: boolean;
  maintenance: boolean;
  message?: string;
}

const BattleWaitingStage = ({ bet, nft, next }: Props) => {
  const { wsConnected } = useWs();
  const alreadyConnected = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>({
    status: false,
    activeFight: false,
    maintenance: false
  });
  const [joined, setJoined] = useState(false);
  const { joinWaitingRoom, subscribeToEvent, unsubscribeFromEvent } =
    useWsToHttp();

  useEffect(() => {
    if (wsConnected) {
      if (!alreadyConnected.current) {
        alreadyConnected.current = true;
        join();
        waitForFight();
        processMaintenance();
      }
    } else {
      alreadyConnected.current = false;
    }
  }, [wsConnected]);

  const join = () => {
    setLoading(true);
    setError({
      status: false,
      activeFight: false,
      maintenance: false,
      message: undefined
    });
    setJoined(false);
    joinWaitingRoom(nft._id, bet)
      .then(() => setJoined(true))
      .catch((err) => {
        if (err.status === 228) {
          setError({ status: true, activeFight: true, maintenance: false });
          window.location.reload();
        } else if (err.status === 307) {
          setError({
            status: true,
            activeFight: false,
            maintenance: true,
            message: err?.error
          });
        } else {
          setError({
            status: true,
            activeFight: false,
            maintenance: false,
            message: err?.error
          });
        }
        unsubscribeFromEvent(wsActionTypes.server.fight.start);
      })
      .finally(() => setLoading(false));
  };

  const waitForFight = () => {
    subscribeToEvent(wsActionTypes.server.fight.start).then(next);
  };

  const processMaintenance = () => {
    subscribeToEvent(wsActionTypes.server.waitingRoom.exitMaintenance).then(
      () => {
        setError({
          status: true,
          maintenance: true,
          activeFight: false,
          message: `We're on maintenance. Please come back later`
        });
        setJoined(false);
        unsubscribeFromEvent(wsActionTypes.server.fight.start);
        unsubscribeFromEvent(wsActionTypes.server.waitingRoom.exitMaintenance);
      }
    );
  };

  return (
    <div className='fight-queue'>
      {(!wsConnected || loading || (error.status && !error.maintenance)) && (
        <Header
          connected={wsConnected}
          joining={loading}
          activeFight={error.status && error.activeFight}
          error={error.status}
        />
      )}
      {(joined || (error.status && (error.activeFight || error.message))) && (
        <Content
          waiting={joined}
          activeFight={error.status && error.activeFight}
          error={error.status ? error.message : undefined}
        />
      )}
      {error.status && !error.activeFight && !error.maintenance && (
        <>
          <br />
          <Button onClick={join}>Retry</Button>
        </>
      )}
    </div>
  );
};

interface HeaderProps {
  connected: boolean;
  joining: boolean;
  activeFight: boolean;
  error: boolean;
}

const Header = ({ connected, joining, activeFight, error }: HeaderProps) => {
  const needDots = connected && (joining || (!error && !activeFight));
  const content = (() => {
    if (!connected) return 'Connection Lost';
    if (joining) return 'Joining the queue';
    if (activeFight) return 'You have an active fight!';
    if (error) return "Can't joing the queue!";
    return 'Waiting for an opponent';
  })();

  return (
    <div className='fight-queue-header'>
      <div className='fqh-text'>{content}</div>
      {needDots && <div className='fqh-dots' />}
    </div>
  );
};

interface ContentProps {
  activeFight: boolean;
  error?: string;
  waiting: boolean;
}

const Content = ({ activeFight, error, waiting }: ContentProps) => {
  const [visible, setVisible] = useState(true);
  const [content, setContent] = useState(getRandomElement(waitingPhrases));
  const usedPhrases = useRef<string[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setContent((prev) => {
          if (usedPhrases.current.length === waitingPhrases.length - 1) {
            usedPhrases.current = [];
          } else {
            usedPhrases.current = usedPhrases.current.concat(prev);
          }
          return getRandomElement(
            waitingPhrases.filter((el) => !usedPhrases.current.includes(el))
          );
        });
        setVisible(true);
      }, 600);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  if (!activeFight && !error && !waiting) return null;

  return (
    <div
      className={clsx(
        'fight-queue-content',
        !visible && 'invisible',
        waiting && 'withFade',
        (error || activeFight) && 'error'
      )}
    >
      {activeFight ? 'Reloading...' : error ? error : content}
    </div>
  );
};

const waitingPhrases = [
  'Casting the perfect match for you…',
  'They are shy, give them some time…',
  "One, two, Freddy's coming for you…",
  'Wait for it…',
  'The battle is coming…',
  'Get ready to kick some ass…',
  'The street is calling for blood…',
  'Coming in a jiffy…',
  'There is someone for everyone…',
  'Hang on a bit more…',
  'It’s gonna be epic…',
  'Tik tok on the clock…'
];

export default BattleWaitingStage;
